"use client";

import { App, ConfigProvider } from "antd";
import type { Locale as AntdLocale } from "antd/es/locale";
import antdThTH from "antd/locale/th_TH";
import dayjs from "dayjs";
import "dayjs/locale/th";
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { IntlError, IntlErrorCode, IntlProvider } from "use-intl";
import thMessages from "@/messages/th.json";
import {
	DEFAULT_LOCALE,
	INTL_LOCALES,
	isLocale,
	Locale,
	LOCALE_STORAGE_KEY,
} from "./config";
import { LOCALE_PENDING_ATTRIBUTE } from "./locale-init-script";

type Messages = typeof thMessages;

type LocaleBundle = {
	messages: Messages;
	/** antd's own strings: pagination, empty tables, date pickers, Modal.confirm buttons */
	antd: AntdLocale;
	/** dayjs locale: month/day names in antd date pickers and dayjs "LL"-style formats */
	dayjs: string;
};

/** What a language chunk delivers; its catalog may lag behind Thai/English. */
type LoadedLocale = { messages: unknown; antd: AntdLocale; dayjs: string };

const THAI_BUNDLE: LocaleBundle = {
	messages: thMessages,
	antd: antdThTH,
	dayjs: "th",
};

// Typed as the Thai catalog, so an English key that goes missing fails tsc.
const loadEnglish = (): Promise<Messages> =>
	import("@/messages/en.json").then((m) => m.default);

// Thai is the default and the language every page is prerendered in, so it
// ships in the main bundle. Each other language is its own chunk, fetched the
// first time someone picks it. Importing a dayjs locale registers it.
const LOADERS: Record<Exclude<Locale, "th">, () => Promise<LoadedLocale>> = {
	en: async () => {
		const [messages, antd] = await Promise.all([
			loadEnglish(),
			import("antd/locale/en_US"),
		]);
		return { messages, antd: antd.default, dayjs: "en" };
	},
	"zh-TW": async () => {
		const [messages, antd] = await Promise.all([
			import("@/messages/zh-TW.json"),
			import("antd/locale/zh_TW"),
			import("dayjs/locale/zh-tw"),
		]);
		return { messages: messages.default, antd: antd.default, dayjs: "zh-tw" };
	},
	vi: async () => {
		const [messages, antd] = await Promise.all([
			import("@/messages/vi.json"),
			import("antd/locale/vi_VN"),
			import("dayjs/locale/vi"),
		]);
		return { messages: messages.default, antd: antd.default, dayjs: "vi" };
	},
	es: async () => {
		const [messages, antd] = await Promise.all([
			import("@/messages/es.json"),
			import("antd/locale/es_ES"),
			import("dayjs/locale/es"),
		]);
		return { messages: messages.default, antd: antd.default, dayjs: "es" };
	},
	fr: async () => {
		const [messages, antd] = await Promise.all([
			import("@/messages/fr.json"),
			import("antd/locale/fr_FR"),
			import("dayjs/locale/fr"),
		]);
		return { messages: messages.default, antd: antd.default, dayjs: "fr" };
	},
	tr: async () => {
		const [messages, antd] = await Promise.all([
			import("@/messages/tr.json"),
			import("antd/locale/tr_TR"),
			import("dayjs/locale/tr"),
		]);
		return { messages: messages.default, antd: antd.default, dayjs: "tr" };
	},
	uk: async () => {
		const [messages, antd] = await Promise.all([
			import("@/messages/uk.json"),
			import("antd/locale/uk_UA"),
			import("dayjs/locale/uk"),
		]);
		return { messages: messages.default, antd: antd.default, dayjs: "uk" };
	},
	ru: async () => {
		const [messages, antd] = await Promise.all([
			import("@/messages/ru.json"),
			import("antd/locale/ru_RU"),
			import("dayjs/locale/ru"),
		]);
		return { messages: messages.default, antd: antd.default, dayjs: "ru" };
	},
};

dayjs.locale(THAI_BUNDLE.dayjs);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

/** Lays `override` over `base`, key by key; anything `override` lacks keeps `base`'s text. */
const withFallback = <T,>(base: T, override: unknown): T => {
	if (!isPlainObject(base) || !isPlainObject(override)) {
		return (typeof override === "string" ? override : base) as T;
	}
	const merged: Record<string, unknown> = { ...base };
	for (const [key, value] of Object.entries(override)) {
		merged[key] = key in base ? withFallback(base[key], value) : value;
	}
	return merged as T;
};

const bundleCache = new Map<Locale, Promise<LocaleBundle>>();
/** Bundles that have finished downloading, for synchronous use. */
const loadedBundles = new Map<Locale, LocaleBundle>([["th", THAI_BUNDLE]]);

/**
 * A language's catalog laid over English, laid over Thai: a key a translation
 * has not caught up with yet shows in English (`pnpm i18n:check` lists them),
 * and only a key missing from English as well — which the check forbids —
 * would show in Thai.
 */
const loadBundle = (locale: Locale): Promise<LocaleBundle> => {
	if (locale === "th") return Promise.resolve(THAI_BUNDLE);
	let pending = bundleCache.get(locale);
	if (!pending) {
		pending = Promise.all([
			LOADERS[locale](),
			locale === "en" ? null : loadEnglish(),
		]).then(([bundle, english]) => {
			const base = english ? withFallback(thMessages, english) : thMessages;
			const full = {
				...bundle,
				messages: withFallback(base, bundle.messages),
			};
			loadedBundles.set(locale, full);
			return full;
		});
		bundleCache.set(locale, pending);
		// A failed chunk download (flaky network, a deploy mid-session) can be retried.
		pending.catch(() => bundleCache.delete(locale));
	}
	return pending;
};

const readSavedLocale = (): Locale | null => {
	try {
		const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
		return isLocale(saved) ? saved : null;
	} catch {
		return null;
	}
};

const saveLocale = (locale: Locale) => {
	try {
		localStorage.setItem(LOCALE_STORAGE_KEY, locale);
	} catch {
		// Storage blocked (private mode, policy): the choice lasts for this tab only.
	}
};

// Start downloading a saved language the moment this module runs — before
// React hydrates — so it is usually in hand by the time it can be applied.
if (typeof window !== "undefined") {
	const saved = readSavedLocale();
	if (saved && saved !== DEFAULT_LOCALE) loadBundle(saved).catch(() => {});
}

const handleIntlError = (error: IntlError) => {
	// Dates are formatted through Intl/dayjs directly, never through use-intl,
	// so its missing-timeZone notice is noise.
	if (error.code === IntlErrorCode.ENVIRONMENT_FALLBACK) return;
	console.error(error);
};

type LocaleContextValue = {
	locale: Locale;
	/** Loads the language if needed, switches the whole UI and remembers the choice. */
	setLocale: (next: Locale) => Promise<void>;
	/** True while a language chunk is downloading. */
	switching: boolean;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export const useLocaleSwitcher = () => {
	const value = useContext(LocaleContext);
	if (!value) {
		throw new Error("useLocaleSwitcher must be used within I18nProvider");
	}
	return value;
};

/** BCP 47 tag of the current UI language, for toLocaleDateString & co. */
export const useIntlLocale = () => INTL_LOCALES[useLocaleSwitcher().locale];

const I18nProvider = ({ children }: { children: ReactNode }) => {
	const [active, setActive] = useState<{
		locale: Locale;
		bundle: LocaleBundle;
	}>({ locale: DEFAULT_LOCALE, bundle: THAI_BUNDLE });
	const [switching, setSwitching] = useState(false);
	// The language last asked for (it may still be downloading) and the one on screen.
	const requested = useRef<Locale>(DEFAULT_LOCALE);
	const shown = useRef<Locale>(DEFAULT_LOCALE);

	const show = useCallback((next: Locale, bundle: LocaleBundle) => {
		dayjs.locale(bundle.dayjs);
		document.documentElement.lang = next;
		shown.current = next;
		setActive({ locale: next, bundle });
	}, []);

	const apply = useCallback(
		async (next: Locale) => {
			requested.current = next;
			let bundle: LocaleBundle;
			try {
				bundle = await loadBundle(next);
			} catch (error) {
				if (requested.current === next) requested.current = shown.current;
				throw error;
			}
			// Two quick picks: only the last one may land.
			if (requested.current !== next) return;
			show(next, bundle);
		},
		[show],
	);

	// The prerendered HTML is Thai, so hydration has to render Thai as well;
	// a saved language is applied right after, while the pre-paint script
	// keeps the page hidden. A layout effect, so that when the bundle is
	// already downloaded the switch re-renders before the first paint.
	// (It runs before the effect below on mount, so `requested` already names
	// the saved language there.)
	useLayoutEffect(() => {
		const saved = readSavedLocale();
		if (!saved || saved === DEFAULT_LOCALE) return;
		const ready = loadedBundles.get(saved);
		if (ready) {
			requested.current = saved;
			// Deliberate: sync re-render with the saved language before first paint.
			// eslint-disable-next-line react-hooks/set-state-in-effect
			show(saved, ready);
			return;
		}
		apply(saved).catch(() => {
			document.documentElement.lang = DEFAULT_LOCALE;
			document.documentElement.removeAttribute(LOCALE_PENDING_ATTRIBUTE);
		});
	}, [apply, show]);

	// Show the page once the language asked for is on screen — this runs
	// after the render that put it there has committed.
	useEffect(() => {
		if (active.locale === requested.current) {
			document.documentElement.removeAttribute(LOCALE_PENDING_ATTRIBUTE);
		}
	}, [active.locale]);

	const setLocale = useCallback(
		async (next: Locale) => {
			setSwitching(true);
			try {
				await apply(next);
				saveLocale(next);
			} finally {
				setSwitching(false);
			}
		},
		[apply],
	);

	const contextValue = useMemo(
		() => ({ locale: active.locale, setLocale, switching }),
		[active.locale, setLocale, switching],
	);

	return (
		<LocaleContext.Provider value={contextValue}>
			<IntlProvider
				locale={active.locale}
				messages={active.bundle.messages}
				onError={handleIntlError}
			>
				<ConfigProvider locale={active.bundle.antd}>
					{/* antd's App: base typography for everything inside, and the
					    context that App.useApp() message/modal/notification read */}
					<App>{children}</App>
				</ConfigProvider>
			</IntlProvider>
		</LocaleContext.Provider>
	);
};

export default I18nProvider;
